import React, { useEffect, useMemo, useState } from 'react'
import shallow from 'zustand/shallow'
import { App, Collapse, ConfigProvider, InputNumber } from 'antd'
import { ExclamationCircleFilled } from '@ant-design/icons'
import { useStore } from '../../store/store'
import { getBoxesByColumnId, getColumnById, getContainerById } from '../../store/lookups'
import { defaultConfiguration, getPrimaryDark, PRIMARY_DARK } from '../../constants/constants'
import { integerInputProps } from '../../utils/inputs'
import { containerColumnGap } from '../../utils/columns'
import AdvancedLayoutSettings from './AdvancedLayoutSettings'
import ContainerTemplatePreview from '../common/ContainerTemplatePreview'
import {
  containerGridPresets,
  templateToLayout,
} from '../../constants/containerTemplates'

// Préréglages de disposition : les grilles du catalogue partagé
// (constants/containerTemplates.js), traduites en descripteur plat
// `[{ size, rows }]`.
//
// Même source que l'onglet Blocs du panneau de gauche : une disposition ajoutée
// là-bas apparaît ici sans rien toucher, et l'utilisateur reconnaît le même
// croquis d'une surface à l'autre.
//
// Seules les grilles, jamais les compositions pré-remplies : ce panneau agit
// sur un conteneur existant, souvent déjà rempli, et y déverser du contenu
// écraserait le travail en cours.
//
// La grille se lit sur deux colonnes et la dernière case revient à
// « Personnalisé » : une grille de plus dans le catalogue décalerait tout et
// lui ferait perdre sa place de fin de liste.
const LAYOUT_PRESETS = containerGridPresets().map((template) => ({
  key: template.id,
  label: template.label,
  columns: templateToLayout(template),
}))

const PREVIEW_HEIGHT = 30

const selector = (containerId) => ({
  containers,
  rowContainers,
  columns,
  boxes,
  configuration,
  updateContainer,
  setContainerColumnGap,
  applyContainerDisposition,
  applyContainerLayout,
  setColumnRowCount,
  splitColumnIntoSubColumns,
}) => {
  const container = getContainerById(containers, containerId)
  // Boxes portées par une colonne, celles de ses lignes imbriquées comprises :
  // c'est ce que les réglages avancés suppriment en retirant la colonne.
  const countBoxesUnder = (columnId) => {
    const column = getColumnById(columns, columnId)
    const nestedColumnIds = (column?.rowContainerIds ?? []).flatMap(
      (rowId) => getContainerById(rowContainers, rowId)?.columnsIds ?? []
    )
    return nestedColumnIds.reduce(
      (total, nestedId) => total + countBoxesUnder(nestedId),
      getBoxesByColumnId(boxes, columnId).length
    )
  }
  return {
    columnsIds: container?.columnsIds,
    // Surcharge d'espacement propre à ce container, undefined tant qu'on n'y a
    // pas touché : la valeur du document s'applique alors.
    containerLineSpacing: container?.lineSpacing,
    columnGap: containerColumnGap(container),
    configuration,
    updateContainer,
    setContainerColumnGap,
    // Une entrée par colonne du container : ses lignes, et si elle a du contenu
    // direct (pour avertir avant une disposition destructive).
    columnsInfos: container?.columnsIds.map((cId) => {
      const column = getColumnById(columns, cId)
      return {
        id: cId,
        size: column?.size ?? 1,
        drawMode: !!column?.drawMode,
        boxCount: getBoxesByColumnId(boxes, cId).length,
        totalBoxCount: countBoxesUnder(cId),
        rows: (column?.rowContainerIds ?? []).map((rowId) => {
          const rowColumnsIds = getContainerById(rowContainers, rowId)?.columnsIds ?? []
          const rowColumns = rowColumnsIds.map((subId) => ({
            id: subId,
            size: getColumnById(columns, subId)?.size ?? 1,
            boxCount: countBoxesUnder(subId),
          }))
          return {
            id: rowId,
            columnsCount: rowColumnsIds.length,
            columns: rowColumns,
            boxCount: rowColumns.reduce((total, sub) => total + sub.boxCount, 0),
          }
        }),
      }
    }),
    applyContainerDisposition,
    applyContainerLayout,
    setColumnRowCount,
    splitColumnIntoSubColumns,
  }
}

const sectionTitleStyle = {
  fontWeight: 700,
  color: PRIMARY_DARK,
  fontSize: 12,
  marginBottom: 6,
}

const subLabelStyle = { color: PRIMARY_DARK, fontSize: 11 }

/**
 * Réglages de disposition du container sélectionné : répartition de ses
 * colonnes, et pour chaque colonne la pile de lignes qu'elle contient
 * (cf. rowContainers). Remplace les menus flottants du canvas, trop à l'étroit
 * pour ces réglages.
 */
const ContainerLayoutPanel = ({ containerId }) => {
  const { modal: modalApi } = App.useApp()
  const {
    columnsIds,
    columnsInfos,
    containerLineSpacing,
    columnGap,
    configuration,
    updateContainer,
    setContainerColumnGap,
    applyContainerDisposition,
    applyContainerLayout,
    setColumnRowCount,
    splitColumnIntoSubColumns,
  } = useStore(selector(containerId), shallow)
  const layoutFocus = useStore(({ layoutFocus }) => layoutFocus)
  const isTemplate = useStore(({ currentDocument }) => currentDocument?.type === 'template')
  // Mêmes droits que la barre du bloc : en proposition, un bloc verrouillé ne
  // se modifie pas, et seul l'ajout de blocs autorisé permet de le découper en
  // lignes ou en sous-colonnes.
  const isLocked = useStore(
    ({ containers }) => !isTemplate && getContainerById(containers, containerId)?.editable === false
  )
  const canSplit = useStore(({ configuration }) => isTemplate || !!configuration?.unlockProposal)
  // Écart entre les lignes de CE container. Le champ écrivait la configuration
  // du document : régler un container les réglait tous, et le réglage global de
  // l'onglet Marges se retrouvait modifié depuis le panneau. Tant qu'on n'y
  // touche pas, le container suit la valeur du document.
  const lineSpacing = containerLineSpacing ?? configuration?.lineSpacing ?? 0
  // Même teinte que la barre flottante du canvas (cf. FloatyStylingWrapper) :
  // c'est la couleur de sélection de l'éditeur, navy en proposition, indigo en
  // template.
  const selectedOutline = `2px solid ${getPrimaryDark(isTemplate)}`

  // Cellule désignée sur le canvas → colonne principale qui la porte. Une
  // sous-colonne de ligne n'a pas de bloc à elle : c'est celui de sa colonne
  // parente qu'il faut ouvrir. Null si la cellule appartient à un autre
  // container, auquel cas ce panel n'a rien à ouvrir.
  const focusedColumnId = useMemo(() => {
    const focusedId = layoutFocus?.columnId
    if (!focusedId) return null
    if (columnsInfos.some((c) => c.id === focusedId)) return focusedId
    return (
      columnsInfos.find((c) =>
        c.rows.some((row) => row.columns.some((sub) => sub.id === focusedId))
      )?.id ?? null
    )
  }, [columnsInfos, layoutFocus])

  // La cellule reste éclairée tant que ce panel est ouvert : le fermer (clic
  // ailleurs, sur une box ou hors du container) l'éteint.
  const clearLayoutFocus = useStore(({ clearLayoutFocus }) => clearLayoutFocus)
  const focusedCellId = focusedColumnId ? layoutFocus?.columnId : null
  useEffect(() => {
    if (!focusedCellId) return
    return () => clearLayoutFocus(focusedCellId)
  }, [focusedCellId, clearLayoutFocus])

  const [advancedKeys, setAdvancedKeys] = useState([])
  useEffect(() => {
    if (!focusedColumnId) return
    setAdvancedKeys((keys) => (keys.includes('advanced') ? keys : [...keys, 'advanced']))
  }, [focusedColumnId, layoutFocus?.nonce])

  if (!columnsIds?.length) return null

  // Une disposition qui retire des colonnes, ou qui ramène une pile de lignes à
  // une colonne simple, supprime du contenu. On confirme dans ce cas seulement.
  const applyPreset = (columns) => {
    if (isLocked) return
    const droppedFromRemovedColumns = columnsInfos
      .slice(columns.length)
      .reduce((total, c) => total + c.boxCount + c.rows.length, 0)
    const droppedFromFlattenedRows = columnsInfos
      .slice(0, columns.length)
      .reduce((total, c, idx) => {
        const targetRows = columns[idx]?.rows ?? 1
        // Ramener N lignes à une colonne simple ne conserve qu'une box ;
        // réduire le nombre de lignes supprime les dernières.
        if (targetRows <= 1) return total + Math.max(c.rows.length - 1, 0)
        return total + Math.max(c.rows.length - targetRows, 0)
      }, 0)

    const apply = () => applyContainerLayout({ containerId, layout: columns })
    if (!droppedFromRemovedColumns && !droppedFromFlattenedRows) {
      apply()
      return
    }
    modalApi.confirm({
      title: 'Appliquer cette disposition ?',
      // Sinon la barre flottante du container passe par-dessus (cf. index.less)
      rootClassName: 'container-layout-modal-root',
      icon: <ExclamationCircleFilled />,
      content:
        'Des colonnes ou des lignes seront supprimées, ainsi que leur contenu.',
      okText: 'Appliquer',
      cancelText: 'Annuler',
      onOk: apply,
    })
  }

  const isCurrent = (columns) =>
    columns.length === columnsInfos.length &&
    columns.every(
      (column, idx) =>
        Math.abs(column.size - columnsInfos[idx].size) < 0.01 &&
        (column.rows ?? 1) === Math.max(columnsInfos[idx].rows.length, 1)
    )

  // L'écart horizontal ne se voit qu'entre deux colonnes, du container ou
  // d'une de ses lignes : sur une colonne seule, il ne ferait que rétrécir la
  // box sans rien espacer.
  const hasSideBySideColumns =
    columnsInfos.length > 1 ||
    columnsInfos.some((c) => c.rows.some((row) => row.columnsCount > 1))

  // Aucun préréglage ne décrit la grille en place : c'est donc qu'elle a été
  // composée à la main.
  const isCustomLayout = !LAYOUT_PRESETS.some((preset) => isCurrent(preset.columns))

  const openAdvanced = () =>
    setAdvancedKeys((keys) => (keys.includes('advanced') ? keys : [...keys, 'advanced']))

  return (
    // data-outside-ignore : sans lui, le mousedown ici désélectionne le
    // container (cf. handleClickOutside dans Container.jsx), ce qui démonte ce
    // panel avant que le click n'atteigne le bouton — l'action ne partait
    // jamais.
    <div
      data-outside-ignore="true"
      style={{ padding: '8px 8px 12px', display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      <ConfigProvider componentDisabled={isLocked}>
      {isLocked && (
        <div style={{ ...subLabelStyle, fontStyle: 'italic' }}>
          Bloc verrouillé : sa disposition ne se modifie pas.
        </div>
      )}
      <div>
        <div style={sectionTitleStyle}>Disposition</div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: 6,
          }}
        >
          {LAYOUT_PRESETS.map((preset) => (
            <div
              key={preset.key}
              className='adder-container-menu-button'
              role='button'
              tabIndex={isLocked ? -1 : 0}
              aria-disabled={isLocked}
              title={
                preset.label ??
                `${preset.columns.length} colonne${preset.columns.length > 1 ? 's' : ''}`
              }
              onClick={() => applyPreset(preset.columns)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  applyPreset(preset.columns)
                }
              }}
              style={{
                padding: 4,
                borderRadius: 4,
                cursor: isLocked ? 'not-allowed' : 'pointer',
                opacity: isLocked ? 0.5 : 1,
                outline: isCurrent(preset.columns) ? selectedOutline : 'none',
              }}
            >
              <ContainerTemplatePreview columns={preset.columns} height={PREVIEW_HEIGHT} />
            </div>
          ))}
          {/* Case d'état autant que raccourci : elle s'allume dès que la grille
              ne correspond plus à aucun préréglage — ce qui arrive au premier
              ajout de ligne ou de colonne dans les réglages avancés — et y mène
              en un clic. */}
          <div
            className='adder-container-menu-button'
            role='button'
            tabIndex={0}
            title='Disposition personnalisée — se règle dans les réglages avancés'
            onClick={openAdvanced}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                openAdvanced()
              }
            }}
            style={{
              padding: 4,
              borderRadius: 4,
              cursor: 'pointer',
              outline: isCustomLayout ? selectedOutline : 'none',
            }}
          >
            <div
              style={{
                // Comme le croquis : la tuile est un conteneur flex, un
                // enfant sans largeur s'y réduirait à son texte.
                width: '100%',
                height: PREVIEW_HEIGHT,
                border: '1.5px dashed #7bb7ff',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                ...subLabelStyle,
              }}
            >
              Personnalisé
            </div>
          </div>
        </div>
      </div>

      <div>
        <div style={sectionTitleStyle}>Espacement vertical</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <InputNumber
            {...integerInputProps}
            size='small'
            // Mêmes bornes que le champ « Marge entre les lignes » des réglages,
            // dont ce champ n'est qu'une version locale : deux plages
            // différentes pour le même écart se contrediraient.
            min={5}
            max={100}
            value={lineSpacing}
            onChange={(value) =>
              updateContainer({
                id: containerId,
                fn: (c) => {
                  c.lineSpacing = value ?? defaultConfiguration.lineSpacing
                },
              })
            }
            style={{ width: 70 }}
          />
          <span style={{ ...subLabelStyle, fontStyle: 'italic' }}>
            px entre les blocs du container
          </span>
        </div>
      </div>

      {hasSideBySideColumns && (
        <div>
          <div style={sectionTitleStyle}>Espacement horizontal</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <InputNumber
              {...integerInputProps}
              size='small'
              min={0}
              max={100}
              value={columnGap}
              onChange={(value) =>
                value != null && setContainerColumnGap({ id: containerId, columnGap: value })
              }
              style={{ width: 70 }}
            />
            <span style={{ ...subLabelStyle, fontStyle: 'italic' }}>
              px entre les colonnes du container
            </span>
          </div>
        </div>
      )}

      <Collapse
        size='small'
        ghost
        activeKey={advancedKeys}
        onChange={setAdvancedKeys}
        items={[
          {
            key: 'advanced',
            label: <span style={sectionTitleStyle}>Réglages avancés</span>,
            children: (
              <AdvancedLayoutSettings
                containerId={containerId}
                columnsInfos={columnsInfos}
                applyContainerDisposition={applyContainerDisposition}
                setColumnRowCount={setColumnRowCount}
                splitColumnIntoSubColumns={splitColumnIntoSubColumns}
                focusedColumnId={focusedColumnId}
                focusedCellId={focusedCellId}
                focusNonce={layoutFocus?.nonce}
                canSplit={canSplit}
              />
            ),
          },
        ]}
      />
      </ConfigProvider>
    </div>
  )
}

export default ContainerLayoutPanel
